// Minimal markdown renderer shared by AnswerSections (deck-builder output)
// and SummaryView (playtest recap). Intentionally not a full markdown
// engine — we support exactly what our LLM prompts emit:
//
//   • `{Card Name}`     → hoverable span with Scryfall image popover
//   • `- ` / `* ` lines → bullet list
//   • `**bold**`        → inline bold
//   • bare `http(s)://` → auto-link, new tab
//
// Everything else renders as plain paragraphs split on blank lines.
//
// Extracted from AnswerSections.tsx so both deck-builder and playtest
// summary pages share the same parsing + styling without duplication.

import type { ReactNode } from "react";
import CardHover from "./CardHover";

export default function Markdown({ text }: { text: string }) {
  const blocks = splitBlocks(text);
  return (
    <>
      {blocks.map((b, i) => {
        if (b.kind === "list") {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1.5 marker:text-fg-subtle">
              {b.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }
        if (b.kind === "hr") {
          return <hr key={i} className="border-t border-border/40 my-2" />;
        }
        return <p key={i}>{renderInline(b.text)}</p>;
      })}
    </>
  );
}

type Block =
  | { kind: "p"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "hr" };

const HR_RE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;

function splitBlocks(text: string): Block[] {
  const lines = text.split("\n");
  const blocks: Block[] = [];
  let paraBuf: string[] = [];
  let listBuf: string[] = [];
  const flushPara = () => {
    const joined = paraBuf.join(" ").trim();
    if (joined) blocks.push({ kind: "p", text: joined });
    paraBuf = [];
  };
  const flushList = () => {
    if (listBuf.length) blocks.push({ kind: "list", items: listBuf });
    listBuf = [];
  };
  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (HR_RE.test(line)) {
      // Horizontal-rule lines (---, ***, ___) inside an answer section —
      // the model emits these as section dividers, but we already convey
      // structure with the surrounding card. Render a muted <hr> rather
      // than a literal "----" paragraph.
      flushPara();
      flushList();
      blocks.push({ kind: "hr" });
      continue;
    }
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      flushPara();
      listBuf.push(bullet[1]!);
    } else if (line.trim() === "") {
      flushPara();
      flushList();
    } else {
      flushList();
      paraBuf.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return blocks;
}

/**
 * Inline pass — splits on `{Card}`, `**bold**`, and URLs.
 * Order matters: we tokenize `{...}` first so a card name isn't eaten by
 * the bold regex.
 */
function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let key = 0;
  const re = /\{([^{}]+?)\}|\*\*([^*]+?)\*\*|(https?:\/\/[^\s)]+)/g;
  let lastIndex = 0;
  for (const m of text.matchAll(re)) {
    const idx = m.index ?? 0;
    if (idx > lastIndex) {
      out.push(text.slice(lastIndex, idx));
    }
    if (m[1] !== undefined) {
      const name = m[1];
      out.push(
        <CardHover key={`c${key++}`} name={name} className="text-fg">
          {name}
        </CardHover>,
      );
    } else if (m[2] !== undefined) {
      // Recurse so `**{Card Name}**` still renders the inner brace-ref
      // as a hoverable card. Plain bold alternation would match the
      // outer `**...**` first and emit the literal `{Card Name}` text.
      out.push(
        <strong key={`b${key++}`} className="font-semibold text-fg">
          {renderInline(m[2])}
        </strong>,
      );
    } else if (m[3] !== undefined) {
      const url = m[3];
      out.push(
        <a
          key={`u${key++}`}
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-accent hover:underline break-all"
        >
          {url}
        </a>,
      );
    }
    lastIndex = idx + m[0].length;
  }
  if (lastIndex < text.length) {
    out.push(text.slice(lastIndex));
  }
  return out;
}
